import React, { useEffect, useState } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { Modal, Button, Checkbox, Select, Link } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePlus } from '@fortawesome/pro-light-svg-icons';
import { SubmissionAuthorContribution } from 'src/contexts/FormContext';
import { contributions, degrees } from '@publishing-engine/domain/fixtures';
import { AuthorFieldValidationOptions } from '@publishing-engine/domain/types/article';

//TODO: User proper contribution types
interface Props {
  isOpen: boolean;
  onClose: () => void;
  onApply: (data: SubmissionAuthorContribution[]) => void;
  defaultContributions: SubmissionAuthorContribution[];
  contributionDegreeField: AuthorFieldValidationOptions;
}

const ContributionModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onApply,
  defaultContributions,
  contributionDegreeField
}) => {
  // Example of selectedContributions: [
  //   'Formal Analysis': 'Lead',
  //   'Conceptualization': 'Equal',
  //   'Investigation': 'Supporting',
  //   ...
  // ]
  const [selectedContributions, setSelectedContributions] = useState<{
    [key: string]: string;
  }>({});

  useEffect(() => {
    const contributionsMap = defaultContributions.reduce(
      (acc, { contribution, degree }) => {
        acc[contribution] = degree;
        return acc;
      },
      {} as { [key: string]: string }
    );
    setSelectedContributions(contributionsMap);
  }, [defaultContributions]);

  const handleContributionChange = (
    contribution: string,
    isChecked: boolean
  ) => {
    setSelectedContributions(prev => {
      const updated = { ...prev };
      if (isChecked) {
        updated[contribution] = '';
      } else {
        delete updated[contribution];
      }
      return updated;
    });
  };

  const handleDegreeChange = (contribution: string, degree: string) => {
    setSelectedContributions(prev => {
      const updated = { ...prev, [contribution]: degree };
      return updated;
    });
  };

  const handleApply = () => {
    const data = Object.entries(selectedContributions).map(
      ([contribution, degree]) => ({
        contribution,
        degree
      })
    );
    onApply(data);
    onClose();
  };

  const handleClose = () => {
    const contributionsMap = defaultContributions.reduce(
      (acc, { contribution, degree }) => {
        acc[contribution] = degree;
        return acc;
      },
      {} as { [key: string]: string }
    );
    setSelectedContributions(contributionsMap);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="755px"
      data-cy="contribution-modal"
    >
      <Flex flexDirection="column" gap={6} padding={8}>
        <Flex flexDirection="row" alignItems="center" gap={2}>
          <FontAwesomeIcon icon={faFilePlus} size="2xl" />
        </Flex>
        <Flex flexDirection="column" gap={2}>
          <Text textStyle="headingGroup">
            <span> Add CRediT Contributions </span>
            <Link
              variant="secondary"
              target="_blank"
              rel="noreferer noopener"
              href={'https://credit.niso.org/'}
              textStyle="headingItem"
            >
              Learn More
            </Link>
          </Text>
          {contributionDegreeField !== 'hidden' && (
            <Text textStyle="textBody">
              Select the tasks this Author performed in this Submission. You may
              also choose their degree of contribution — lead, equal, or
              supporting — for each task.
            </Text>
          )}
        </Flex>

        <Flex flexDirection="column">
          <Text textStyle="headingItem">CRediT Contributions</Text>
          {contributions.map((contribution, index) => (
            <Flex
              key={index}
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              paddingY={2}
              borderBottom="1px solid"
              borderColor="neutralPrimaryBorder"
            >
              <Checkbox
                isChecked={contribution in selectedContributions}
                onChange={e =>
                  handleContributionChange(contribution, e.target.checked)
                }
                ariaLabel="Contribution Checkbox"
                id={`contribution-${contribution}`}
                name="contribution"
                data-cy={`contribution-checkbox-${contribution}`}
              >
                {contribution}
              </Checkbox>

              {contributionDegreeField !== 'hidden' && (
                <Select
                  onChange={e => {
                    handleDegreeChange(contribution, e.target.value);
                  }}
                  value={selectedContributions[contribution] || ''}
                  minWidth="180px"
                  maxWidth="240px"
                  data-cy={`degree-select-${contribution}`}
                >
                  <option value="" hidden disabled>
                    Select a Degree
                  </option>
                  {degrees.map((degree, idx) => (
                    <option key={idx} value={degree}>
                      {degree}
                    </option>
                  ))}
                </Select>
              )}
            </Flex>
          ))}
        </Flex>

        <Flex flexDirection="row" gap={2} justifyContent="end">
          <Button
            variant="secondary"
            onClick={handleClose}
            data-cy="cancel-button"
          >
            Back to Edit Authors
          </Button>
          <Button
            variant="primary"
            onClick={handleApply}
            data-cy="apply-button"
          >
            Apply
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default ContributionModal;
